import { useFormContext, useFormField } from '@calendly/form'
import { FieldLabel } from '@calendly/form/parts'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { IconGripVertical } from '@calendly/ui/components/glyphs/IconGripVertical'
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash'
import { IconButton } from '@calendly/ui/components/icon-button'
import { LabelText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { CheckboxField as StandaloneCheckboxField } from '@calendly/ui/form/checkbox-field'
import { RadioField } from '@calendly/ui/form/radio-field'
import { colorIconDisabled } from '@calendly/ui/theme'
import { useCallback } from 'react'

import {
  CardContent,
  decorativeDragIconCss,
  ExpandedCard,
  ExpandedCardBody,
  ExpandedCardFooter,
  ExpandedCardHeader,
  expandedCardFooterActionsCss,
  expandedCardHeaderTitleCss,
  expandedCardSettingSectionCss,
  nameFormatOptionsCss,
} from './styles'

const getNameFormatOptions = () => ([
  {
    label: I18n.t('publisher.event_type_editor_crud.booking_page.full_name'),
    value: 'combined',
  },
  {
    label: I18n.t('publisher.event_type_editor_crud.booking_page.first_and_last_name'),
    value: 'separated',
  },
])

export const NameQuestionEditor = ({
  draftSnapshot,
  onCancel,
  onDone,
}) => {
  const { setFieldValue } = useFormContext()
  const [{ value: nameFormat }, _, { setValue: setNameFormat }] = useFormField({ name: 'nameFormat' })

  const handleDone = useCallback(() => {
    onDone()
  }, [onDone])

  const handleCancel = useCallback(() => {
    if (draftSnapshot != null) {
      setFieldValue('nameFormat', draftSnapshot)
    }
    onCancel()
  }, [draftSnapshot, onCancel, setFieldValue])

  return (
    <ExpandedCard data-testid="name-question-editor">
      <ExpandedCardHeader>
        <span className={decorativeDragIconCss} aria-hidden>
          <UiIcon color={colorIconDisabled} icon={IconGripVertical} size="medium" title="" />
        </span>
        <CardContent>
          <LabelText className={expandedCardHeaderTitleCss} size="large">
            {I18n.t('publisher.event_type_editor_crud.booking_page.name_question_title')}
          </LabelText>
        </CardContent>
        <IconButton
          appearance="ghost"
          aria-label={I18n.t('publisher.action.close')}
          disabled
          icon={IconTrash}
          size="small"
          title={I18n.t('publisher.action.close')}
        />
      </ExpandedCardHeader>
      <ExpandedCardBody>
        <FieldLabel
          isRequired
          requiredMark="asterisk"
          text={I18n.t('publisher.event_type_editor_crud.booking_page.name_format_label')}
        />
        <div
          className={nameFormatOptionsCss}
          data-testid="name-format-options"
          role="radiogroup"
          aria-label={I18n.t('publisher.event_type_editor_crud.booking_page.name_format_label')}
        >
          {getNameFormatOptions().map(({ label, value }) => {
            const inputId = `name-format-${value}`

            return (
              <RadioField
                key={value}
                checked={nameFormat === value}
                id={inputId}
                label={label}
                name="nameFormat"
                value={value}
                onChange={() => setNameFormat(value)}
              />
            )
          })}
        </div>
      </ExpandedCardBody>
      <div className={expandedCardSettingSectionCss}>
        {/* Standalone field: Name is always required in product; keep checked like Figma. */}
        <StandaloneCheckboxField
          checked
          disabled
          label={I18n.t('publisher.event_types.invitee_questions.required_label')}
          onChange={() => {}}
        />
      </div>
      <ExpandedCardFooter className={expandedCardFooterActionsCss}>
        <Button appearance="ghost" size="small" type="button" onClick={handleCancel}>
          {I18n.t('publisher.action.cancel')}
        </Button>
        <Button appearance="primary" size="small" type="button" onClick={handleDone}>
          {I18n.t('publisher.action.done')}
        </Button>
      </ExpandedCardFooter>
    </ExpandedCard>
  )
}
